import { useEffect, useState } from 'react';

import {
  ApiRef,
  BackstageIdentityApi,
  IdentityApi,
  OpenIdConnectApi,
  ProfileInfoApi,
  SessionApi,
  useApi,
} from '@backstage/core-plugin-api';
import { UserIdentity } from '@backstage/core-components';
import '../../design-system/cyrius-login.css';

const ASSET_BASE =
  '/brand-assets';

type CyriusAuthApi = OpenIdConnectApi &
  ProfileInfoApi &
  BackstageIdentityApi &
  SessionApi;

interface CyriusProvider {
  id: string;
  title: string;
  message: string;
  apiRef: ApiRef<CyriusAuthApi>;
}

export interface CyriusSignInPageProps {
  provider: CyriusProvider;
  onSignInSuccess: (identityApi: IdentityApi) => void;
}

interface HeadMetadata {
  selector: string;
  tag: 'meta' | 'link';
  attributes: Record<string, string>;
}

const browserMetadata: HeadMetadata[] = [
  {
    selector: 'link[rel="canonical"]',
    tag: 'link',
    attributes: {
      rel: 'canonical',
      href: 'https://cyrius.aranox.com.br/',
    },
  },
  {
    selector: 'link[rel="icon"][sizes="any"]',
    tag: 'link',
    attributes: {
      rel: 'icon',
      href: `${ASSET_BASE}/cyrius-icone/cyrius_favicon.ico`,
      sizes: 'any',
    },
  },
  {
    selector: 'link[rel="icon"][sizes="16x16"]',
    tag: 'link',
    attributes: {
      rel: 'icon',
      type: 'image/png',
      sizes: '16x16',
      href: `${ASSET_BASE}/cyrius-icone/cyrius_favicon-16x16.png`,
    },
  },
  {
    selector: 'link[rel="icon"][sizes="32x32"]',
    tag: 'link',
    attributes: {
      rel: 'icon',
      type: 'image/png',
      sizes: '32x32',
      href: `${ASSET_BASE}/cyrius-icone/cyrius_favicon-32x32.png`,
    },
  },
  {
    selector: 'link[rel="icon"][sizes="48x48"]',
    tag: 'link',
    attributes: {
      rel: 'icon',
      type: 'image/png',
      sizes: '48x48',
      href: `${ASSET_BASE}/cyrius-icone/cyrius_favicon-48x48.png`,
    },
  },
  {
    selector: 'link[rel="apple-touch-icon"]',
    tag: 'link',
    attributes: {
      rel: 'apple-touch-icon',
      sizes: '180x180',
      href: `${ASSET_BASE}/cyrius-icone/cyrius_apple-touch-icon.png`,
    },
  },
  {
    selector: 'link[rel="manifest"]',
    tag: 'link',
    attributes: {
      rel: 'manifest',
      href: `${ASSET_BASE}/cyrius-icone/cyrius_site.webmanifest`,
    },
  },
  {
    selector: 'meta[name="description"]',
    tag: 'meta',
    attributes: {
      name: 'description',
      content:
        'Portal CYRIUS — supervisão integrada, telemetria e inteligência operacional no ecossistema ARANOX.',
    },
  },
  {
    selector: 'meta[name="theme-color"]',
    tag: 'meta',
    attributes: {
      name: 'theme-color',
      content: '#061329',
    },
  },
  {
    selector: 'meta[name="msapplication-TileColor"]',
    tag: 'meta',
    attributes: {
      name: 'msapplication-TileColor',
      content: '#061329',
    },
  },
  {
    selector: 'meta[property="og:title"]',
    tag: 'meta',
    attributes: {
      property: 'og:title',
      content: 'Portal CYRIUS | ARANOX Digital Platform',
    },
  },
  {
    selector: 'meta[property="og:description"]',
    tag: 'meta',
    attributes: {
      property: 'og:description',
      content:
        'Supervisão integrada, telemetria e inteligência operacional no ecossistema ARANOX.',
    },
  },
  {
    selector: 'meta[property="og:type"]',
    tag: 'meta',
    attributes: {
      property: 'og:type',
      content: 'website',
    },
  },
  {
    selector: 'meta[property="og:url"]',
    tag: 'meta',
    attributes: {
      property: 'og:url',
      content: 'https://cyrius.aranox.com.br/',
    },
  },
  {
    selector: 'meta[property="og:site_name"]',
    tag: 'meta',
    attributes: {
      property: 'og:site_name',
      content: 'ARANOX',
    },
  },
  {
    selector: 'meta[property="og:locale"]',
    tag: 'meta',
    attributes: {
      property: 'og:locale',
      content: 'pt_BR',
    },
  },
  {
    selector: 'meta[property="og:image"]',
    tag: 'meta',
    attributes: {
      property: 'og:image',
      content: `${ASSET_BASE}/social/cyrius-og.jpg`,
    },
  },
  {
    selector: 'meta[property="og:image:secure_url"]',
    tag: 'meta',
    attributes: {
      property: 'og:image:secure_url',
      content: `${ASSET_BASE}/social/cyrius-og.jpg`,
    },
  },
  {
    selector: 'meta[property="og:image:width"]',
    tag: 'meta',
    attributes: {
      property: 'og:image:width',
      content: '1200',
    },
  },
  {
    selector: 'meta[property="og:image:height"]',
    tag: 'meta',
    attributes: {
      property: 'og:image:height',
      content: '630',
    },
  },
  {
    selector: 'meta[property="og:image:type"]',
    tag: 'meta',
    attributes: {
      property: 'og:image:type',
      content: 'image/jpeg',
    },
  },
  {
    selector: 'meta[property="og:image:alt"]',
    tag: 'meta',
    attributes: {
      property: 'og:image:alt',
      content: 'CYRIUS — Supervisão, Operação e Inteligência do ecossistema ARANOX',
    },
  },
  {
    selector: 'meta[name="twitter:card"]',
    tag: 'meta',
    attributes: {
      name: 'twitter:card',
      content: 'summary_large_image',
    },
  },
  {
    selector: 'meta[name="twitter:title"]',
    tag: 'meta',
    attributes: {
      name: 'twitter:title',
      content: 'Portal CYRIUS | ARANOX Digital Platform',
    },
  },
  {
    selector: 'meta[name="twitter:description"]',
    tag: 'meta',
    attributes: {
      name: 'twitter:description',
      content:
        'Supervisão integrada, telemetria e inteligência operacional.',
    },
  },
  {
    selector: 'meta[name="twitter:image"]',
    tag: 'meta',
    attributes: {
      name: 'twitter:image',
      content: `${ASSET_BASE}/social/cyrius-og.jpg`,
    },
  },
];

const ecosystem = [
  {
    id: 'aranox',
    label: 'ARANOX',
    src: `${ASSET_BASE}/aranox-logo-header.webp`,
  },
  {
    id: 'vitalis',
    label: 'VITALIS',
    src: `${ASSET_BASE}/vitalis_logo_branco_transp_v2.png`,
  },
  {
    id: 'cyrius',
    label: 'CYRIUS',
    src: `${ASSET_BASE}/cyrius_logo_branco_transp.png`,
  },
  {
    id: 'agrion',
    label: 'AGRION',
    src: `${ASSET_BASE}/agrion_logo_branco_transp.png`,
  },
  {
    id: 'sentrix',
    label: 'SENTRIX',
    src: `${ASSET_BASE}/sentrix_logo_branco_transp.png`,
  },
  {
    id: 'tes',
    label: 'TES',
    src: `${ASSET_BASE}/tes_logo_branco_transp.png`,
  },
];

function applyMetadata() {
  document.title = 'Portal CYRIUS | ARANOX Digital Platform';
  document.documentElement.lang = 'pt-BR';

  browserMetadata.forEach(item => {
    let element = document.head.querySelector<HTMLElement>(item.selector);

    if (!element) {
      element = document.createElement(item.tag);
      document.head.appendChild(element);
    }

    Object.entries(item.attributes).forEach(([name, value]) => {
      element?.setAttribute(name, value);
    });
  });
}

export function CyriusSignInPage({
  provider,
  onSignInSuccess,
}: CyriusSignInPageProps) {
  const authApi = useApi(provider.apiRef);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    applyMetadata();
  }, []);

  const handleSignIn = async () => {
    setLoading(true);
    setErrorMessage('');

    try {
      const identityResponse = await authApi.getBackstageIdentity({
        instantPopup: true,
      });

      if (!identityResponse) {
        throw new Error(
          'O provedor corporativo não está configurado para autenticação.',
        );
      }

      const profile = await authApi.getProfile();

      onSignInSuccess(
        UserIdentity.create({
          identity: identityResponse.identity,
          authApi,
          profile,
        }),
      );
    } catch (error) {
      const normalizedError =
        error instanceof Error
          ? error
          : new Error('Não foi possível iniciar a autenticação.');

      if (normalizedError.name !== 'PopupRejectedError') {
        setErrorMessage(normalizedError.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="cyrius-login">
      <div className="cyrius-login__ambient" aria-hidden="true">
        <span className="cyrius-login__orb cyrius-login__orb--one" />
        <span className="cyrius-login__orb cyrius-login__orb--two" />
        <span className="cyrius-login__grid" />
        <span className="cyrius-login__wave cyrius-login__wave--one" />
        <span className="cyrius-login__wave cyrius-login__wave--two" />
      </div>

      <section className="cyrius-login__content">
        <div className="cyrius-login__brand">
          <p className="cyrius-login__eyebrow">ARANOX DIGITAL PLATFORM</p>

          <img
            className="cyrius-login__brand-logo"
            src={`${ASSET_BASE}/cyrius_logo_branco_transp.png`}
            alt="CYRIUS"
            width="909"
            height="691"
            decoding="async"
            fetchPriority="high"
          />

          <div className="cyrius-login__brand-message">
            <h1>Supervisão, Operação e Inteligência</h1>
            <span className="cyrius-login__accent-line" aria-hidden="true" />
            <p>
              Supervisão integrada, telemetria e inteligência operacional aplicadas
              aos processos do ecossistema <strong>ARANOX</strong>.
            </p>
          </div>
        </div>

        <section
          className="cyrius-login__card"
          aria-labelledby="cyrius-access-title"
        >
          <div className="cyrius-login__security">
            <span className="cyrius-login__security-icon" aria-hidden="true">
              ✓
            </span>
            Ambiente corporativo seguro
          </div>

          <div className="cyrius-login__card-heading">
            <p>Bem-vindo</p>
            <h2 id="cyrius-access-title">Acesse o Portal CYRIUS</h2>
            <span>
              Use sua identidade corporativa ARANOX para continuar.
            </span>
          </div>

          <img
            className="cyrius-login__card-logo"
            src={`${ASSET_BASE}/cyrius_logo_branco_transp.png`}
            alt=""
            width="512"
            height="512"
            decoding="async"
          />

          <button
            className="cyrius-login__button"
            type="button"
            onClick={handleSignIn}
            disabled={loading}
            aria-busy={loading}
          >
            <span className="cyrius-login__button-lock" aria-hidden="true">
              {loading ? '•••' : '↗'}
            </span>
            <span>
              {loading ? 'CONECTANDO...' : 'ENTRAR NO PORTAL CYRIUS'}
            </span>
          </button>

          {errorMessage && (
            <div className="cyrius-login__error" role="alert">
              {errorMessage}
            </div>
          )}

          <div className="cyrius-login__privacy">
            <span aria-hidden="true">◆</span>
            Acesso corporativo protegido
          </div>
        </section>
      </section>

      <nav
        className="cyrius-login__ecosystem"
        aria-label="Ecossistema ARANOX"
      >
        <div className="cyrius-login__ecosystem-track">
          {ecosystem.map(item => (
            <div className="cyrius-login__ecosystem-item" key={item.id}>
              <img
                src={item.src}
                alt=""
                width="512"
                height="512"
                loading="lazy"
                decoding="async"
              />
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      </nav>
    </main>
  );
}
